import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Send, RefreshCw, MessageSquare, Paperclip, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "in21_reporter_token";

function client() {
  const c = axios.create({ baseURL: API });
  c.interceptors.request.use((cfg) => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) cfg.headers.Authorization = `Bearer ${t}`;
    return cfg;
  });
  return c;
}

export default function ReporterChatTab() {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [imgUrl, setImgUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const scroller = useRef(null);

  const load = () => client().get("/reporter/messages").then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);
  useEffect(() => { if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight; }, [msgs]);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await client().post("/reporter/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImgUrl(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
    } catch { toast.error("अपलोड नहीं हुआ"); }
    finally { setUploading(false); }
  };

  const send = async () => {
    if (!text.trim() && !imgUrl) return;
    setBusy(true);
    try {
      await client().post("/reporter/messages", { content: text.trim(), image_url: imgUrl || null });
      setText(""); setImgUrl(""); load();
    } catch (e) { toast.error("संदेश नहीं भेजा जा सका"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col h-[70vh]" data-testid="reporter-chat-tab">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-blue-600"/>
        <div className="font-bold text-[#0F172A] text-sm">एडिटर से बातचीत</div>
        <button onClick={load} className="ml-auto p-1.5 rounded hover:bg-slate-100" title="रीलोड"><RefreshCw className="w-3.5 h-3.5"/></button>
      </div>
      <div ref={scroller} className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-slate-50" data-testid="reporter-chat-body">
        {msgs.length === 0 && <div className="text-center text-slate-400 italic text-sm py-8">अभी कोई संदेश नहीं। नीचे लिखकर एडिटर से पूछें।</div>}
        {msgs.map((m) => (
          <div key={m.id} className={`flex ${m.sender === "reporter" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] px-3 py-2 rounded-lg shadow-sm ${
              m.sender === "reporter" ? "bg-emerald-500 text-white rounded-br-none" : "bg-white text-slate-800 border border-slate-200 rounded-bl-none"
            }`}>
              {m.sender === "admin" && <div className="text-[10px] uppercase tracking-widest text-amber-600 font-bold mb-1">एडिटर JAKIR MOMIN</div>}
              {m.image_url && <img src={m.image_url} alt="" className="mb-1.5 rounded max-w-full max-h-56 object-cover"/>}
              {m.audio_url && (
                <div className="mb-1.5">
                  <audio src={m.audio_url} controls className="h-9 w-full max-w-[260px]"/>
                  {m.audio_transcript && <div className="mt-1 text-[11px] italic opacity-80">📝 {m.audio_transcript}</div>}
                </div>
              )}
              {m.content && <div className="text-sm whitespace-pre-line leading-relaxed">{m.content}</div>}
              <div className={`text-[10px] mt-1 ${m.sender === "reporter" ? "text-emerald-100" : "text-slate-500"}`}>{new Date(m.created_at).toLocaleString("hi-IN")}</div>
            </div>
          </div>
        ))}
      </div>
      {imgUrl && (
        <div className="px-3 py-2 border-t border-slate-100 flex items-center gap-2 bg-slate-50">
          <img src={imgUrl} alt="" className="w-12 h-12 rounded object-cover"/>
          <span className="text-xs text-slate-600 flex-1">तस्वीर तैयार</span>
          <button onClick={() => setImgUrl("")} className="p-1 hover:bg-slate-200 rounded"><X className="w-4 h-4"/></button>
        </div>
      )}
      <div className="p-3 border-t border-slate-200 flex gap-2">
        <label className="p-2 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer" title="तस्वीर">
          <Paperclip className={`w-4 h-4 ${uploading ? "text-amber-500 animate-pulse" : "text-slate-600"}`}/>
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid="reporter-chat-attach"/>
        </label>
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder="एडिटर से पूछें…"
          className="flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
          data-testid="reporter-chat-input"/>
        <button onClick={send} disabled={busy || (!text.trim() && !imgUrl)} className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-4 rounded-md flex items-center gap-1.5 disabled:opacity-50" data-testid="reporter-chat-send">
          <Send className="w-4 h-4"/>
        </button>
      </div>
    </div>
  );
}
